import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  LightMode,
  DarkMode,
  OpenInNew,
  Menu as MenuIcon,
  BugReport,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "../theme/ThemeContext";
import { getApiDocsHref } from "../api";
import { APP_NAME, NAV_LINKS, REPORT_BUG_LABEL } from "../constants";

export function Navbar() {
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  const go = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            width: "100%",
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 2, md: 3 },
            justifyContent: "flex-start",
            gap: { xs: 0.5, sm: 1, md: 2 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Lexend', sans-serif",
              fontWeight: 900,
              fontSize: { xs: "0.9375rem", sm: "1.125rem", md: undefined },
              letterSpacing: "-0.05em",
              textTransform: "uppercase",
              color: "text.primary",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => {
              navigate("/");
              closeDrawer();
            }}
          >
            {APP_NAME}
          </Typography>

          {isWide ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: { sm: 1 },
                  alignItems: "center",
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                {NAV_LINKS.map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <Button
                      key={link.path}
                      onClick={() => navigate(link.path)}
                      sx={{
                        color: isActive ? "text.primary" : "text.secondary",
                        fontWeight: isActive ? 700 : 500,
                        fontFamily: "'Lexend', sans-serif",
                        fontSize: "14px",
                        textTransform: "none",
                        borderBottom: isActive ? 2 : 0,
                        borderColor: "text.primary",
                        borderRadius: 0,
                        px: 1.5,
                        py: 0.5,
                        minWidth: "auto",
                      }}
                    >
                      {link.label}
                    </Button>
                  );
                })}
                <Button
                  component="a"
                  href={getApiDocsHref()}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew sx={{ fontSize: 18 }} aria-hidden />}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    fontFamily: "'Lexend', sans-serif",
                    fontSize: "14px",
                    textTransform: "none",
                    px: 1.5,
                    py: 0.5,
                    minWidth: "auto",
                  }}
                  aria-label="API dokumentacija - otvara novu karticu"
                >
                  API
                </Button>
                <Button
                  component="a"
                  href="https://bugs.pinehood.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<BugReport sx={{ fontSize: 18 }} aria-hidden />}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    fontFamily: "'Lexend', sans-serif",
                    fontSize: "14px",
                    textTransform: "none",
                    px: 1.5,
                    py: 0.5,
                    minWidth: "auto",
                  }}
                  aria-label={`${REPORT_BUG_LABEL} – otvara novu karticu`}
                >
                  {REPORT_BUG_LABEL}
                </Button>
              </Box>

              <Box sx={{ flex: 1, minWidth: 8 }} />
            </>
          ) : (
            <Box sx={{ flex: 1, minWidth: 8 }} />
          )}

          <IconButton
            size="small"
            onClick={toggleTheme}
            sx={{ color: "text.primary", flexShrink: 0 }}
            aria-label="Promijeni temu"
          >
            {mode === "light" ? <DarkMode /> : <LightMode />}
          </IconButton>

          {!isWide && (
            <IconButton
              size="medium"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: "text.primary", flexShrink: 0, ml: 0.5 }}
              aria-label="Otvaranje izbornika"
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: 280,
            maxWidth: "85vw",
            pt: 1,
            bgcolor: "background.paper",
          },
        }}
      >
        <List component="nav" dense sx={{ px: 1 }}>
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <ListItemButton
                key={link.path}
                selected={isActive}
                onClick={() => go(link.path)}
                sx={{
                  borderRadius: 1,
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                  },
                }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{
                    fontFamily: "'Lexend', sans-serif",
                    fontWeight: isActive ? 700 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            component="a"
            href="https://bugs.pinehood.tech/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeDrawer}
            sx={{
              borderRadius: 1,
              gap: 1,
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={REPORT_BUG_LABEL}
              primaryTypographyProps={{
                fontFamily: "'Lexend', sans-serif",
                fontWeight: 500,
              }}
              sx={{ flex: 1, my: 0 }}
            />
            <BugReport fontSize="small" color="action" aria-hidden />
          </ListItemButton>
          <ListItemButton
            component="a"
            href={getApiDocsHref()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeDrawer}
            sx={{
              borderRadius: 1,
              gap: 1,
              alignItems: "center",
            }}
          >
            <ListItemText
              primary="API dokumentacija"
              primaryTypographyProps={{
                fontFamily: "'Lexend', sans-serif",
                fontWeight: 500,
              }}
              sx={{ flex: 1, my: 0 }}
            />
            <OpenInNew fontSize="small" color="action" aria-hidden />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
